/**
 * Timezone Utility Module
 * 
 * Handles timezone conversions and date formatting based on user's state/locale.
 * Default timezone: America/New_York (NYC)
 */

// US State to timezone mapping
export const STATE_TIMEZONES: Record<string, string> = {
  // Eastern Time
  'CT': 'America/New_York',
  'DE': 'America/New_York',
  'FL': 'America/New_York',
  'GA': 'America/New_York',
  'ME': 'America/New_York',
  'MD': 'America/New_York',
  'MA': 'America/New_York',
  'MI': 'America/New_York',
  'NH': 'America/New_York',
  'NJ': 'America/New_York',
  'NY': 'America/New_York',
  'NC': 'America/New_York',
  'OH': 'America/New_York',
  'PA': 'America/New_York',
  'RI': 'America/New_York',
  'SC': 'America/New_York',
  'VT': 'America/New_York',
  'VA': 'America/New_York',
  'WV': 'America/New_York',
  
  // Central Time
  'AL': 'America/Chicago',
  'AR': 'America/Chicago',
  'IL': 'America/Chicago',
  'IN': 'America/Chicago', // Note: Some parts are ET
  'IA': 'America/Chicago',
  'KS': 'America/Chicago',
  'KY': 'America/Chicago', // Note: Eastern part is ET
  'LA': 'America/Chicago',
  'MN': 'America/Chicago',
  'MS': 'America/Chicago',
  'MO': 'America/Chicago',
  'NE': 'America/Chicago',
  'ND': 'America/Chicago',
  'OK': 'America/Chicago',
  'SD': 'America/Chicago',
  'TN': 'America/Chicago', // Note: Eastern part is ET
  'TX': 'America/Chicago',
  'WI': 'America/Chicago',
  
  // Mountain Time
  'AZ': 'America/Phoenix', // Arizona doesn't observe DST
  'CO': 'America/Denver',
  'ID': 'America/Denver',
  'MT': 'America/Denver',
  'NM': 'America/Denver',
  'UT': 'America/Denver',
  'WY': 'America/Denver',
  
  // Pacific Time
  'CA': 'America/Los_Angeles',
  'NV': 'America/Los_Angeles',
  'OR': 'America/Los_Angeles',
  'WA': 'America/Los_Angeles',
  
  // Alaska
  'AK': 'America/Anchorage',
  
  // Hawaii
  'HI': 'America/Honolulu',
  
  // Territories
  'PR': 'America/Puerto_Rico',
  'VI': 'America/St_Thomas',
  'GU': 'Pacific/Guam',
  'AS': 'Pacific/Pago_Pago',
  'MP': 'Pacific/Saipan',
};

export const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * Get timezone for a given US state code
 * @param stateCode - Two-letter state code (e.g., 'NY', 'CA')
 * @returns IANA timezone string
 */
export function getTimezoneForState(stateCode?: string): string {
  if (!stateCode) return DEFAULT_TIMEZONE;
  return STATE_TIMEZONES[stateCode.toUpperCase()] || DEFAULT_TIMEZONE;
}

/**
 * Get user's timezone based on their profile state
 * Falls back to NYC timezone if state is not available
 * @param user - User object with optional state property
 * @returns IANA timezone string
 */
export function getUserTimezone(user?: { state?: string } | null): string {
  return getTimezoneForState(user?.state);
}

/**
 * Convert a date string to user's local timezone and format for date input (YYYY-MM-DD)
 * @param dateString - ISO date string or date-only string
 * @param timezone - IANA timezone string
 * @returns Formatted date string (YYYY-MM-DD)
 */
export function toLocalDateInput(dateString: string | Date, timezone: string = DEFAULT_TIMEZONE): string {
  if (!dateString) return '';
  
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  // Use Intl.DateTimeFormat to get the date in the user's timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  const parts = formatter.formatToParts(date);
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  
  return `${year}-${month}-${day}`;
}

/**
 * Format a calendar-only date (no time-of-day component) for display as
 * MM/DD/YYYY — e.g. classdate, cyclestart/cycleend, issueDate/expiryDate,
 * effectiveDate, archivedDate. These represent a plain day ("the class was
 * on the 8th"), never a specific moment in time, so NO timezone conversion
 * may ever be applied to them.
 *
 * Why this matters: a bare date like "2026-09-08" gets stored as UTC
 * midnight (2026-09-08T00:00:00.000Z). Running that through ANY timezone
 * conversion — the browser's local zone via toLocaleDateString(), or even
 * an explicit IANA zone via Intl.DateTimeFormat — silently rolls it back
 * to the previous day for every US timezone (all behind UTC), since
 * midnight UTC is always evening-of-the-prior-day in America/*. There is
 * no "correct" timezone that fixes this — the fix is to never convert at
 * all, and read the year/month/day directly off the stored ISO string.
 */
export function formatCalendarDate(dateInput?: string | Date | null): string {
  if (!dateInput) return '';
  const iso = typeof dateInput === 'string' ? dateInput : dateInput.toISOString();
  const [year, month, day] = iso.split('T')[0].split('-');
  if (!year || !month || !day) return '';
  return `${month}/${day}/${year}`;
}

/**
 * @deprecated for calendar-only dates (classdate, cyclestart/cycleend,
 * issueDate/expiryDate, effectiveDate, archivedDate) — use
 * formatCalendarDate() instead. Applying timezone conversion to those
 * fields causes an off-by-one-day bug for every US timezone (see
 * formatCalendarDate's docs). Only appropriate for a value that
 * genuinely represents a specific moment in time.
 * @param dateString - ISO date string or Date object
 * @param timezone - IANA timezone string
 * @returns Formatted date string (MM/DD/YYYY)
 */
export function formatDateDisplay(dateString: string | Date, timezone: string = DEFAULT_TIMEZONE): string {
  if (!dateString) return '';
  
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  
  return formatter.format(date);
}

/**
 * Format date and time for display in user's timezone
 * @param dateString - ISO date string or Date object
 * @param timezone - IANA timezone string
 * @param includeSeconds - Whether to include seconds in the output
 * @returns Formatted datetime string
 */
export function formatDateTimeDisplay(
  dateString: string | Date, 
  timezone: string = DEFAULT_TIMEZONE,
  includeSeconds: boolean = false
): string {
  if (!dateString) return '';
  
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
    hour12: true,
  });
  
  return formatter.format(date);
}

/**
 * Convert a local date input (YYYY-MM-DD) to ISO string considering user's timezone
 * This ensures that when a user selects a date, it's interpreted in their timezone
 * @param dateString - Date string in YYYY-MM-DD format
 * @param timezone - IANA timezone string
 * @returns ISO string in UTC
 */
export function fromLocalDateInput(dateString: string, timezone: string = DEFAULT_TIMEZONE): string {
  if (!dateString) return '';
  
  // Parse the date string as if it's in the user's timezone
  const [year, month, day] = dateString.split('-').map(Number);
  
  // Create a date string that can be parsed in the specified timezone
  const dateInTimezone = new Date(
    new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T12:00:00`)
      .toLocaleString('en-US', { timeZone: timezone })
  );
  
  return dateInTimezone.toISOString();
}

/**
 * Get current date in user's timezone (YYYY-MM-DD format)
 * @param timezone - IANA timezone string
 * @returns Date string in YYYY-MM-DD format
 */
export function getCurrentDateInTimezone(timezone: string = DEFAULT_TIMEZONE): string {
  const now = new Date();
  return toLocalDateInput(now, timezone);
}

/**
 * Calculate days remaining until a date, considering timezone
 * @param endDate - End date string or Date object
 * @param timezone - IANA timezone string
 * @returns Number of days remaining (negative if past)
 */
export function getDaysRemaining(endDate: string | Date, timezone: string = DEFAULT_TIMEZONE): number {
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  const today = new Date(getCurrentDateInTimezone(timezone));
  today.setHours(0, 0, 0, 0);
  
  const diffMs = end.getTime() - today.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

/**
 * Format a relative time string (e.g., "2 days ago", "in 5 hours")
 * @param dateString - ISO date string or Date object
 * @param timezone - IANA timezone string
 * @returns Relative time string
 */
export function formatRelativeTime(dateString: string | Date, timezone: string = DEFAULT_TIMEZONE): string {
  if (!dateString) return '';
  
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  const now = new Date();
  
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);
  
  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  if (diffWeek < 4) return `${diffWeek} week${diffWeek !== 1 ? 's' : ''} ago`;
  if (diffMonth < 12) return `${diffMonth} month${diffMonth !== 1 ? 's' : ''} ago`;
  return `${diffYear} year${diffYear !== 1 ? 's' : ''} ago`;
}
